// @ts-nocheck
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage";
import Tab, { TabProps } from "@insite/mobius/Tab";
import TabGroup, { TabGroupStyleProps, TabGroupWrapperProps } from "@insite/mobius/TabGroup";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import parse from "html-react-parser";
import * as React from "react";
import { FC } from "react";
import { css, FlattenSimpleInterpolation } from "styled-components";

const enum fields {
    widgetTabs = "widgetTabs",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.widgetTabs]: TabModel[];
    };
}

interface TabModel {
    fields: {
        heading: string;
        contentImage: string;
        content: string;
    };
}

type Props = OwnProps;

export interface ContentTabsRightStyles {
    tabHeadings?: TabGroupStyleProps;
    tabContainer?: TypographyPresentationProps;
    tabContent?: TabGroupStyleProps;
    tabGroupWrapper?: TabGroupWrapperProps;
    gridContainer?: GridContainerProps;
    gridItem?: GridItemProps;
}

export const contentTabsRightStyles: ContentTabsRightStyles = {
    tabGroupWrapper: {
        css: css`
            flex-direction: row;
            div:nth-child(2) {
                flex-basis: 70%;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            flex-direction: column;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    tabHeadings: {
        css: css`
            flex-direction: column;
            justify-content: flex-start;
            flex-basis: 30%;
            overflow-x: unset;
            word-wrap: normal;
            word-break: normal;
        `,
    },
    tabContent: {
        css: css`
            border: 0;
            padding: ${pxToRem(5, 25)};
        `,
    },
    tabContainer: {
        color: "primary.contrast",
    },
    gridContainer: {
        offsetCss: css`
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                        `,
                        css`
                            flex-direction: column;
                        `,
                        css`
                            flex-direction: row;
                            flex-wrap: nowrap !important;
                        `,
                        null,
                        null,
                    ],
                    "min",
                );
            }}
        `,
    },
    gridItem: {
        width: 12,
        css: css`
            flex-wrap: wrap;
            flex-direction: column;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            flex-basis: 70%;
                            word-wrap: normal;
                            word-break: normal;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "min",
                );
            }}
        `,
    },
};

const tabGroup = contentTabsRightStyles.tabHeadings?.css;
const tabContent = contentTabsRightStyles.tabContent?.css;
const wrapper = contentTabsRightStyles.tabGroupWrapper?.css;

const imageCss: FlattenSimpleInterpolation = css`
    justify-content: start;
` as FlattenSimpleInterpolation;

const tabCss: FlattenSimpleInterpolation = css`
    text-transform: uppercase;
    text-align: left;
    padding: ${pxToRem(15)};
    margin-bottom: ${pxToRem(5)};
    background: ${(props: TabProps) => (props.selected ? getColor("primary") : getColor("border.light"))};
    color: ${(props: TabProps) => (props.selected ? getColor("primary.contrast") : getColor("text.main"))};
    &:hover {
        border-bottom: none;
    }
    &:focus {
        outline: rgb(0, 153, 255) solid ${pxToRem(2)};
    }
    border-bottom: none;
    font-weight: bold;
    font-size: 1em;
` as FlattenSimpleInterpolation;

const ContentTabsRight: FC<Props> = ({ fields: { widgetTabs } }) => {
    const tabs = widgetTabs?.map((field, index) => {
        const key = `${field.fields.heading}-${index}`;
        return (
            <Tab key={key} css={tabCss} headline={<>{field.fields.heading}</>} tabKey={key}>
                <GridContainer {...contentTabsRightStyles.gridContainer}>
                    <GridItem {...contentTabsRightStyles.gridItem}>
                        <LazyImage
                            css={imageCss}
                            src={field.fields.contentImage}
                            altText={`${field.fields.heading}-image`}
                        />
                    </GridItem>
                    <GridItem {...contentTabsRightStyles.gridItem}>
                        {parse(field.fields.content, parserOptions)}
                    </GridItem>
                </GridContainer>
            </Tab>
        );
    });
    return <TabGroup cssOverrides={{ tabContent, tabGroup, wrapper }}>{tabs}</TabGroup>;
};

const definition: WidgetDefinition = {
    group: "Basic",
    icon: "columns-3",
    fieldDefinitions: [
        {
            name: fields.widgetTabs,
            editorTemplate: "ListField",
            getDisplay: ({ fields: { heading } }) => heading,
            defaultValue: [],
            fieldType: "Translatable",
            fieldDefinitions: [
                {
                    name: "heading",
                    displayName: "Title",
                    editorTemplate: "TextField",
                    defaultValue: "",
                    isRequired: true,
                },
                {
                    name: "contentImage",
                    displayName: "Image",
                    editorTemplate: "ImagePickerField",
                    defaultValue: "",
                    isRequired: true,
                },
                {
                    name: "content",
                    displayName: "Content",
                    editorTemplate: "RichTextField",
                    defaultValue: "",
                    isRequired: true,
                },
            ],
        },
    ],
};

const widgetModule: WidgetModule = {
    component: ContentTabsRight,
    definition,
};

export default widgetModule;
